import type Handsontable from 'handsontable/base';
import { cloneDeep } from 'lodash';

import type { CellDataChange } from '../../../../../common/table/TableTypes';
import { ON_MILANOTE_MARK_DIRTY } from '../../sync/milanoteHotToReduxSessionHookNames';
import { applyExactHotTableCellDataChanges } from '../../utils/hotInstanceUtils/hotTableUpdateUtils';
import {
    createUndoRedoActionCellDataChanges,
    HotTableUndoRedoActionType,
    type UndoRedoActionWithCellData,
    type UndoRedoRichCellDataChange,
} from './hotTableUndoRedoRichCellDataUtils';

type UndoRedoCallback = () => void;
type UndoRedoSelection = Array<[number, number, number, number]>;
type HotUndoRedoPluginWithDone = {
    done?: (wrappedAction: () => HotTableMetaCellDataUndoRedoAction, source?: string) => void;
};
type HotTableMetaCellDataUndoRedoAction = UndoRedoActionWithCellData & {
    actionType: HotTableUndoRedoActionType.MILANOTE_META_CELL_DATA_CHANGE;
    selected: UndoRedoSelection;
    source: string;
    undo: (instance: Handsontable, callback: UndoRedoCallback) => void;
    redo: (instance: Handsontable, callback: UndoRedoCallback) => void;
};

/**
 * Applies exact cell-data snapshots while replaying a custom metadata-only undo/redo action.
 */
const applyMetaCellDataUndoRedoChange = (
    hotInstance: Handsontable,
    cellDataChanges: UndoRedoRichCellDataChange[],
    source: string,
    changeKey: 'previousCellData' | 'newCellData',
): void => {
    applyExactHotTableCellDataChanges(
        hotInstance,
        cellDataChanges.map(({ row, col, previousCellData, newCellData }) => [
            row,
            col,
            previousCellData,
            cloneDeep(changeKey === 'previousCellData' ? previousCellData : newCellData),
        ]),
        source,
    );

    hotInstance.runHooks(ON_MILANOTE_MARK_DIRTY, source);
};

/**
 * Pushes a metadata-only cell-data change onto Handsontable's undo stack.
 */
export const addHotTableCellDataUndoAction = (
    hotInstance: Handsontable,
    cellDataChanges: CellDataChange[],
    source: string,
): void => {
    if (!cellDataChanges.length) return;

    const undoRedoPlugin = hotInstance.getPlugin('undoRedo') as HotUndoRedoPluginWithDone | undefined;
    if (!undoRedoPlugin?.done) return;

    const milanoteCellDataChanges = cloneDeep(createUndoRedoActionCellDataChanges(cellDataChanges));
    const selected: UndoRedoSelection = hotInstance.getSelected() || [
        [cellDataChanges[0][0], Number(cellDataChanges[0][1]), cellDataChanges[0][0], Number(cellDataChanges[0][1])],
    ];

    // This will choose either the "previousCellData" or the "newCellData" properties to apply when performing
    //  either an undo or redo, respectively
    undoRedoPlugin.done(
        (): HotTableMetaCellDataUndoRedoAction => ({
            actionType: HotTableUndoRedoActionType.MILANOTE_META_CELL_DATA_CHANGE,
            milanoteCellDataChanges,
            selected,
            source,
            undo: (instance: Handsontable, undoneCallback: UndoRedoCallback) => {
                applyMetaCellDataUndoRedoChange(instance, milanoteCellDataChanges, source, 'previousCellData');

                if (selected.length) instance.selectCells(selected, false, false);

                undoneCallback();
            },
            redo: (instance: Handsontable, redoneCallback: UndoRedoCallback) => {
                applyMetaCellDataUndoRedoChange(instance, milanoteCellDataChanges, source, 'newCellData');

                if (selected.length) instance.selectCells(selected, false, false);

                redoneCallback();
            },
        }),
        source,
    );
};
